import { Component, ElementRef, Input, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { Photo } from '@capacitor/camera';
import { faChevronLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { AlertController, IonicSlides, ModalController, Platform } from '@ionic/angular/lazy';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import Swiper from 'swiper';

@Component({
  selector: 'in2-image-gallery-modal',
  templateUrl: './image-gallery.modal.html',
  styleUrls: ['./image-gallery.modal.scss'],
  standalone: false,
})
export class ImageGalleryModal implements OnInit, OnDestroy {
  readonly slides = viewChild.required<ElementRef<{ swiper: Swiper }>>('slides');

  @Input() public internalUrls?: Photo[];
  @Input() public externalUrls: string[] = [];
  @Input() public startIndex = 0;

  // Icons
  public faChevronLeft = faChevronLeft;
  public faTrash = faTrash;

  // Conf slider
  public slideOpts = {
    initialSlide: this.startIndex,
    speed: 300,
  };
  public swiperModules = [IonicSlides];

  // Internal
  public deletableMode = false;
  public readonly internalUrlsWithoutDeletedOnes = signal<Photo[]>([]);
  private readonly deletedInternalUrls: Photo[] = [];
  private backButton$?: Subscription;

  constructor(
    private readonly modalController: ModalController,
    private readonly alertController: AlertController,
    private readonly translateService: TranslateService,
    private readonly platform: Platform
  ) {}

  public ionViewWillEnter() {
    this.slides().nativeElement.swiper.slideTo(this.startIndex, 0);
  }

  public ngOnInit(): void {
    if (this.internalUrls !== undefined) {
      this.deletableMode = true;
      this.internalUrlsWithoutDeletedOnes.set([...this.internalUrls]);
    }

    this.backButton$ = this.platform.backButton.subscribeWithPriority(9999, () => {
      this.dismissModal();
    });
  }

  public ngOnDestroy(): void {
    this.backButton$?.unsubscribe();
  }

  public async presentAlertConfirmDelete() {
    const alert = await this.alertController.create({
      header: 'Confirm!',
      message: this.translateService.instant('gallery.deletionConfirmationSpeech'),
      buttons: [
        {
          text: this.translateService.instant('gallery.delete'),
          cssClass: 'danger',
          handler: () => {
            this.deletePicture();
          },
        },
        {
          text: this.translateService.instant('gallery.cancel'),
          role: 'cancel',
        },
      ],
    });

    await alert.present();
  }

  public deletePicture() {
    const index = this.slides().nativeElement.swiper.activeIndex;
    const supprimee = this.internalUrlsWithoutDeletedOnes()[index];
    if (supprimee) {
      this.deletedInternalUrls.push(supprimee);
      this.internalUrlsWithoutDeletedOnes.update(photos => photos.filter(photo => photo !== supprimee));
    }

    if (this.internalUrlsWithoutDeletedOnes().length === 0) {
      this.dismissModal();
    }
  }

  public dismissModal() {
    this.modalController.dismiss(this.deletedInternalUrls);
  }
}
